package top.wcpe.allincore.ui.runtime.material

import kotlin.math.max
import kotlin.math.min

enum class MaterialInteractionState {
    HOVERED,
    PRESSED,
    FOCUSED,
    DISABLED,
    SELECTED,
    ACTIVE,
}

/** FR-UI-024：只走 runtime uniform 的动态材质参数，严禁进入静态缓存 key。 */
data class MaterialDynamicParams(
    val brightness: Float = 1f,
    val contrast: Float = 1f,
    val glow: Float = 0f,
    val grooveDepth: Float = 0f,
    val bevel: Float = 0f,
    val distortion: Float = 0f,
    val historyOpacity: Float = 1f,
    val runeGlow: Float = 0f,
    val translateY: Float = 0f,
) {
    fun interpolateTo(target: MaterialDynamicParams, progress: Float): MaterialDynamicParams {
        val start = normalized(this, DEFAULT_PARAMS)
        val end = normalized(target, start)
        val t = normalizedProgress(progress)
        return MaterialDynamicParams(
            brightness = lerp(start.brightness, end.brightness, t),
            contrast = lerp(start.contrast, end.contrast, t),
            glow = lerp(start.glow, end.glow, t),
            grooveDepth = lerp(start.grooveDepth, end.grooveDepth, t),
            bevel = lerp(start.bevel, end.bevel, t),
            distortion = lerp(start.distortion, end.distortion, t),
            historyOpacity = lerp(start.historyOpacity, end.historyOpacity, t),
            runeGlow = lerp(start.runeGlow, end.runeGlow, t),
            translateY = lerp(start.translateY, end.translateY, t),
        )
    }
}

data class MaterialStateSnapshot(val progress: Map<MaterialInteractionState, Float>) {
    companion object {
        fun single(state: MaterialInteractionState, progress: Float): MaterialStateSnapshot {
            val normalized = normalizedProgress(progress)
            return if (normalized > 0f) {
                MaterialStateSnapshot(mapOf(state to normalized))
            } else {
                EMPTY_STATE_SNAPSHOT
            }
        }
    }
}

class MaterialStateAnimator(
    base: MaterialDynamicParams = MaterialDynamicParams(),
    states: Map<MaterialInteractionState, MaterialDynamicParams>,
) {
    private val base = normalized(base, DEFAULT_PARAMS)
    private val states = states.mapValues { (_, target) -> normalized(target, this.base) }

    fun resolve(snapshot: MaterialStateSnapshot): MaterialDynamicParams {
        return STATE_RESOLUTION_ORDER.fold(base) { current, state ->
            val target = states[state] ?: return@fold current
            when (val progress = normalizedProgress(snapshot.progress[state] ?: 0f)) {
                0f -> current
                1f -> target
                else -> current.interpolateTo(target, progress)
            }
        }
    }
}

class MaterialStateProgressTracker(
    private val enterDurationSeconds: Float = DEFAULT_DURATION_SECONDS,
    private val releaseDurationSeconds: Float = DEFAULT_DURATION_SECONDS,
) {
    private val progress = linkedMapOf<MaterialInteractionState, Float>()

    init {
        require(enterDurationSeconds.isFinite() && enterDurationSeconds > 0f) { "进入时长必须是大于零的有限值" }
        require(releaseDurationSeconds.isFinite() && releaseDurationSeconds > 0f) { "退出时长必须是大于零的有限值" }
    }

    fun set(state: MaterialInteractionState, value: Float) {
        val normalized = normalizedProgress(value)
        if (normalized > 0f) progress[state] = normalized else progress.remove(state)
    }

    fun next(activeStates: Set<MaterialInteractionState>, deltaSeconds: Float): MaterialStateSnapshot {
        val elapsed = deltaSeconds.takeIf { it.isFinite() && it > 0f } ?: 0f
        MaterialInteractionState.entries.forEach { state ->
            update(state, state in activeStates, elapsed)
        }
        return snapshot()
    }

    fun snapshot(): MaterialStateSnapshot = MaterialStateSnapshot(progress.toMap())

    private fun update(state: MaterialInteractionState, active: Boolean, elapsed: Float) {
        val current = progress[state] ?: 0f
        val duration = if (active) enterDurationSeconds else releaseDurationSeconds
        val step = normalizedStep(elapsed, duration)
        val next = if (active) min(1f, current + step) else max(0f, current - step)
        if (next > 0f) progress[state] = next else progress.remove(state)
    }
}

class MaterialStateProgressStore<K : Any>(
    private val enterDurationSeconds: Float = DEFAULT_DURATION_SECONDS,
    private val releaseDurationSeconds: Float = DEFAULT_DURATION_SECONDS,
) {
    private val trackers = linkedMapOf<K, MaterialStateProgressTracker>()

    init {
        require(enterDurationSeconds.isFinite() && enterDurationSeconds > 0f) { "进入时长必须是大于零的有限值" }
        require(releaseDurationSeconds.isFinite() && releaseDurationSeconds > 0f) { "退出时长必须是大于零的有限值" }
    }

    fun advance(key: K, activeStates: Set<MaterialInteractionState>, deltaSeconds: Float): MaterialStateSnapshot {
        val existing = trackers[key]
        if (existing == null && activeStates.isEmpty()) return EMPTY_STATE_SNAPSHOT
        val tracker = existing ?: MaterialStateProgressTracker(enterDurationSeconds, releaseDurationSeconds)
            .also { trackers[key] = it }
        val snapshot = tracker.next(activeStates, deltaSeconds)
        if (snapshot.progress.isEmpty()) trackers.remove(key)
        return snapshot
    }

    fun remove(key: K) {
        trackers.remove(key)
    }

    fun clear() {
        trackers.clear()
    }

    fun size(): Int = trackers.size
}

private const val DEFAULT_DURATION_SECONDS = 0.2f
private val DEFAULT_PARAMS = MaterialDynamicParams()
private val EMPTY_STATE_SNAPSHOT = MaterialStateSnapshot(emptyMap())

/** 由低到高叠加，后处理的高优先级状态按自身进度覆盖低优先级结果。 */
private val STATE_RESOLUTION_ORDER = listOf(
    MaterialInteractionState.HOVERED,
    MaterialInteractionState.FOCUSED,
    MaterialInteractionState.SELECTED,
    MaterialInteractionState.ACTIVE,
    MaterialInteractionState.PRESSED,
    MaterialInteractionState.DISABLED,
)

private fun normalized(value: MaterialDynamicParams, fallback: MaterialDynamicParams): MaterialDynamicParams =
    MaterialDynamicParams(
        brightness = finiteOr(value.brightness, fallback.brightness, 1f).coerceIn(0.2f, 2f),
        contrast = finiteOr(value.contrast, fallback.contrast, 1f).coerceIn(0f, 2f),
        glow = finiteOr(value.glow, fallback.glow, 0f).coerceIn(0f, 2f),
        grooveDepth = finiteOr(value.grooveDepth, fallback.grooveDepth, 0f).coerceIn(0f, 1f),
        bevel = finiteOr(value.bevel, fallback.bevel, 0f).coerceIn(0f, 1f),
        distortion = finiteOr(value.distortion, fallback.distortion, 0f).coerceIn(0f, 1f),
        historyOpacity = finiteOr(value.historyOpacity, fallback.historyOpacity, 1f).coerceIn(0f, 1f),
        runeGlow = finiteOr(value.runeGlow, fallback.runeGlow, 0f).coerceIn(0f, 2f),
        translateY = finiteOr(value.translateY, fallback.translateY, 0f),
    )

private fun finiteOr(value: Float, fallback: Float, default: Float): Float = when {
    value.isFinite() -> value
    fallback.isFinite() -> fallback
    else -> default
}

private fun normalizedProgress(value: Float): Float = if (value.isFinite()) {
    value.coerceIn(0f, 1f)
} else {
    0f
}

private fun normalizedStep(elapsed: Float, duration: Float): Float {
    val step = elapsed / duration
    return when {
        step.isFinite() -> step.coerceIn(0f, 1f)
        elapsed > 0f -> 1f
        else -> 0f
    }
}

private fun lerp(start: Float, end: Float, progress: Float): Float = when (progress) {
    0f -> start
    1f -> end
    else -> start + (end - start) * progress
}
